import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Users, 
  DollarSign, 
  TrendingUp, 
  FileText,
  Phone,
  Mail,
  Calendar,
  MoreVertical,
  Plus,
  Search,
  LogOut,
  Settings,
  Bell,
  LayoutDashboard,
  Wrench,
  Briefcase,
  Receipt,
  ClipboardList,
  Send,
  BarChart2,
  PieChart as PieChartIcon,
  Navigation,
  CreditCard,
  MessageCircle,
  MapPin,
  Menu,
  CheckCircle,
  UserPlus,
  Repeat,
} from 'lucide-react';
import { use3CXIntegration } from '../components/IncomingCallPopup';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import { normalizeAdminTab } from '../lib/adminAppUtils';
import * as crmBackend from '../services/crmBackendService';
import ProcessPaymentDialog from '../components/ProcessPaymentDialog';
import EstimateDetailDialog from '../components/EstimateDetailDialog';
import InvoiceDetailDialog from '../components/InvoiceDetailDialog';
import TechnicianMultiSelect from '../components/TechnicianMultiSelect';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

// ERPNext host that serves /files/... attachments (customer signature images).
const ERP_HOST = 'https://erp.aaairrigationservice.com';

const getCurrentUser = () => {
  const token = localStorage.getItem('authToken');
  let username = null;
  if (token) { try { username = JSON.parse(atob(token)).username; } catch {} }
  const role = localStorage.getItem('userRole') || 'admin';
  const name = username || 'Admin';
  return { name, role: role.charAt(0).toUpperCase() + role.slice(1), avatar: name[0].toUpperCase() };
};

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = getCurrentUser();

  // Hard guard — techs must not see the admin CRM
  useEffect(() => {
    if (localStorage.getItem('userRole') === 'tech') {
      navigate('/tech/dashboard', { replace: true });
    }
  }, [navigate]);
  const BOT_ORIGIN = 'https://bot.aaairrigationservice.com';
  const [activeTab, setActiveTab] = useState(() => normalizeAdminTab(location.hash));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  useEffect(() => {
    const normalized = normalizeAdminTab(location.hash);
    setActiveTab((current) => (current === normalized ? current : normalized));
  }, [location.hash]);

  // Flow Bot AI — auto-unlock so an already-logged-in admin isn't asked to log in twice
  const botIframeRef = useRef(null);
  const handleBotIframeLoad = useCallback(async () => {
    if (!botIframeRef.current?.contentWindow) return;
    try {
      const token = await crmBackend.getBotToken();
      botIframeRef.current.contentWindow.postMessage(
        { type: 'boatman-ai-auth', token },
        BOT_ORIGIN
      );
    } catch (err) {
      console.error('Could not auto-unlock Flow Bot AI', err);
    }
  }, [BOT_ORIGIN]);

  useEffect(() => {
    if (activeTab === 'bot') {
      handleBotIframeLoad();
    }
  }, [activeTab, handleBotIframeLoad]);

  // Lead → estimate dialog
  const [leadEstOpen, setLeadEstOpen] = useState(false);
  const [leadEstLead, setLeadEstLead] = useState(null);
  const [leadEstForm, setLeadEstForm] = useState({ address: '', date: '', time: '', technician: '', rate: '' });
  const [leadEstSaving, setLeadEstSaving] = useState(false);

  // Payment dialog
  const [payOpen, setPayOpen] = useState(false);
  const [payInvoice, setPayInvoice] = useState(null);
  const [payForm, setPayForm] = useState({ amount: '', mode: 'Cash', reference_no: '' });
  const [paySaving, setPaySaving] = useState(false);
  // Process Payment (live card charge) — separate from Record Payment above,
  // which only logs a payment already taken elsewhere.
  const [chargeInvoice, setChargeInvoice] = useState(null);

  const [chatOpen, setChatOpen] = useState(false);
  const [chatText, setChatText] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { toast } = useToast();
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [allCustomersLoaded, setAllCustomersLoaded] = useState(false);
  const [loadingAllCustomers, setLoadingAllCustomers] = useState(false);
  const [dashboardStats, setDashboardStats] = useState({
    total_contacts: 0,
    active_leads: 0,
    monthly_revenue: 0,
    conversion_rate: 0,
    connected: false,
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [estimatesLoading, setEstimatesLoading] = useState(false);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [estimateDetailOpen, setEstimateDetailOpen] = useState(false);
  const [sendingEstimate, setSendingEstimate] = useState(false);
  const [completingJob, setCompletingJob] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [estimateEditForm, setEstimateEditForm] = useState({});
  const [estimateSaving, setEstimateSaving] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDetailOpen, setInvoiceDetailOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    status: 'Active',
    deal_value: ''
  });
  const [addrQuery, setAddrQuery] = useState('');
  const [addrSuggestions, setAddrSuggestions] = useState([]);
  const [addrLoading, setAddrLoading] = useState(false);
  const [addrOpen, setAddrOpen] = useState(false);

  const API_BASE = useMemo(() => (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, ''), []);

  const filteredContacts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase().replace(/\D/g, '') || searchQuery.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter(c => {
      const name = (c.name || '').toLowerCase();
      const phone = (c.phone || '').replace(/\D/g, '');
      const email = (c.email || '').toLowerCase();
      // phone digit match
      if (q.replace(/\D/g, '') && phone.includes(q.replace(/\D/g, ''))) return true;
      // name match (supports last name search)
      if (name.includes(q)) return true;
      // email match
      if (email.includes(q)) return true;
      return false;
    });
  }, [contacts, searchQuery]);

  const loadDashboard = async () => {
    setContactsLoading(true);
    try {
      const data = await crmBackend.getDashboard();
      setDashboardStats({
        total_contacts: data.total_contacts ?? 0,
        active_leads: data.active_leads ?? 0,
        monthly_revenue: data.monthly_revenue ?? 0,
        conversion_rate: data.conversion_rate ?? 0,
        conversion_paid: data.conversion_paid ?? null,
        conversion_leads: data.conversion_leads ?? null,
        job_metrics: data.job_metrics || null,
        connected: Boolean(data.connected),
      });
      setContacts(data.contacts || []);
      setRecentActivity(data.recent_activity || []);
      if (!data.connected && data.error) {
        toast({
          title: 'ERP data unavailable',
          description: data.error,
          variant: 'destructive',
        });
      }
    } catch (err) {
      console.error('Failed to load dashboard', err);
      toast({
        title: 'Could not load dashboard',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setContactsLoading(false);
    }
  };

  // The initial dashboard load only fetches the first 200 customers (kept light for
  // the Overview tab). The Contacts tab needs the complete list, so fetch every
  // customer once, lazily, the first time that tab is opened, and merge it in —
  // leaving the already-complete leads portion of `contacts` untouched.
  const loadAllCustomers = async () => {
    setLoadingAllCustomers(true);
    try {
      const result = await crmBackend.getAllCustomers();
      const allCustomers = result.data || [];
      setContacts(prev => [...allCustomers, ...prev.filter(c => c.status === 'Lead')]);
      setAllCustomersLoaded(true);
    } catch (err) {
      toast({
        title: 'Could not load all contacts',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setLoadingAllCustomers(false);
    }
  };

  const loadCalendar = async () => {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const endDate = new Date(now.getFullYear(), now.getMonth() + 2, 0).toISOString().split('T')[0];
    try {
      const result = await crmBackend.getScheduledQuotations(startDate, endDate);
      setAppointments((result.data || []).map(q => ({
        id: q.name,
        title: `${q.party_name} — ${q.custom_appointment_status || 'Pending Confirmation'}`,
        date: q.custom_scheduled_date,
        time: q.custom_scheduled_time || '',
        location: q.custom_service_address || '',
        technician: q.custom_technician || '',
        status: q.custom_appointment_status || 'Pending Confirmation',
        jobStatus: crmBackend.normalizeJobStatus(q.custom_job_status),
        amount: q.grand_total,
      })));
    } catch (err) {
      console.error('Failed to load calendar', err);
    }
  };

  const loadInvoices = async () => {
    setInvoicesLoading(true);
    try {
      const result = await crmBackend.getInvoices();
      setInvoices(result.data || []);
    } catch (err) {
      console.error('Failed to load invoices', err);
    } finally {
      setInvoicesLoading(false);
    }
  };

  const loadEstimates = async () => {
    setEstimatesLoading(true);
    try {
      const result = await crmBackend.getEstimates();
      setEstimates(result.data || []);
    } catch (err) {
      console.error('Failed to load estimates', err);
    } finally {
      setEstimatesLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
    loadCalendar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === 'invoices' && invoices.length === 0) loadInvoices();
    if (activeTab === 'analytics' && !analytics) {
      setAnalyticsLoading(true);
      crmBackend.getAnalytics().then(setAnalytics).catch(() => {}).finally(() => setAnalyticsLoading(false));
    }
    if (activeTab === 'estimates' && estimates.length === 0) loadEstimates();
    if (activeTab === 'contacts' && !allCustomersLoaded && !loadingAllCustomers) loadAllCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const openEstimateDetail = async (est) => {
    try {
      const result = await crmBackend.getEstimateDetail(est.name);
      const doc = result.data || est;
      setSelectedEstimate(doc);
      setEstimateEditForm({
        custom_appointment_status: doc.custom_appointment_status || '',
        custom_scheduled_date: doc.custom_scheduled_date || '',
        custom_scheduled_time: doc.custom_scheduled_time || '',
        custom_technician: doc.custom_technician || '',
        custom_job_status: crmBackend.normalizeJobStatus(doc.custom_job_status),
      });
      setEstimateDetailOpen(true);
    } catch (err) {
      toast({ title: 'Could not load estimate', description: err.message, variant: 'destructive' });
    }
  };

  // Refresh the open estimate (totals/status) after the embedded items editor
  // saves an add/edit/remove — the editor's own list is already live, but
  // this dialog's Total/status chip and the estimates table row are not.
  const refreshSelectedEstimate = async () => {
    if (!selectedEstimate?.name) return;
    try {
      const result = await crmBackend.getEstimateDetail(selectedEstimate.name);
      const doc = result.data;
      if (doc) setSelectedEstimate(doc);
      setEstimates(prev => prev.map(e => (e.name === doc?.name ? { ...e, ...doc } : e)));
    } catch { /* non-fatal — dialog list already reflects the save */ }
  };

  const saveEstimate = async () => {
    if (!selectedEstimate) return;
    setEstimateSaving(true);
    try {
      await crmBackend.updateEstimate(selectedEstimate.name, estimateEditForm);
      toast({ title: 'Estimate updated' });
      setEstimateDetailOpen(false);
      setEstimates([]);
      await loadEstimates();
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setEstimateSaving(false);
    }
  };

  // ── Manual-only estimate/job/invoice actions (nothing here auto-fires) ──

  // Emails the estimate with Approve/Decline links (ERP send_estimate_to_customer).
  const handleSendEstimate = async () => {
    if (!selectedEstimate) return;
    setSendingEstimate(true);
    try {
      await crmBackend.sendEstimateToCustomer(selectedEstimate.name);
      setSelectedEstimate(e => ({ ...e, custom_approval_status: 'Sent for Approval' }));
      toast({ title: 'Estimate sent', description: `${selectedEstimate.name} emailed for customer approval.` });
      setEstimates([]);
      await loadEstimates();
    } catch (err) {
      toast({ title: 'Could not send estimate', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSendingEstimate(false);
    }
  };

  // Marks the job complete and produces the invoice — same two-step path the tech
  // app uses (job status → Work Order → manual_approve_estimate → SO + Invoice),
  // so office-completed jobs land in ERP identically to field-completed ones.
  const handleCompleteEstimateJob = async () => {
    if (!selectedEstimate) return;
    setCompletingJob(true);
    try {
      await crmBackend.updateJobStatus(selectedEstimate.name, 'Completed');
      let woId = null;
      try {
        const existing = await crmBackend.getWorkOrderDetail(selectedEstimate.name);
        woId = existing?.data?.wo_id || null;
      } catch {
        // No Work Order yet — expected for a job completed straight from the office.
      }
      if (!woId) {
        const created = await crmBackend.createWorkOrder(selectedEstimate.name, selectedEstimate.custom_technician || '', 'office');
        woId = created.wo_id;
      }
      const invResult = await crmBackend.generateWOInvoice(woId, selectedEstimate.custom_technician || '');
      setEstimateEditForm(f => ({ ...f, custom_job_status: 'Completed' }));
      toast({
        title: 'Job completed',
        description: invResult.invoice_name ? `Invoice ${invResult.invoice_name} created.` : 'Job marked complete.',
      });
      setEstimates([]);
      await loadEstimates();
    } catch (err) {
      toast({ title: 'Could not complete job', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setCompletingJob(false);
    }
  };

  // Emails the invoice with the Pay Now link + Zelle block (ERP send_invoice_to_customer).
  const handleSendInvoice = async () => {
    if (!selectedInvoice) return;
    setSendingInvoice(true);
    try {
      await crmBackend.sendInvoiceToCustomer(selectedInvoice.name);
      toast({ title: 'Invoice sent', description: `${selectedInvoice.name} emailed to the customer for payment.` });
    } catch (err) {
      toast({ title: 'Could not send invoice', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSendingInvoice(false);
    }
  };

  const handleOpenPayment = (inv) => {
    setPayInvoice(inv);
    setPayForm({ amount: String(inv.outstanding_amount || inv.grand_total || ''), mode: 'Cash', reference_no: '' });
    setInvoiceDetailOpen(false);
    setPayOpen(true);
  };

  const handleSavePayment = async () => {
    if (!payInvoice) return;
    setPaySaving(true);
    try {
      await crmBackend.payInvoice(payInvoice.name, {
        amount: parseFloat(payForm.amount) || 0,
        mode_of_payment: payForm.mode,
        reference_no: payForm.reference_no,
      });
      toast({ title: 'Payment recorded' });
      setPayOpen(false);
      setInvoices([]);
      await loadInvoices();
    } catch (err) {
      toast({ title: 'Payment failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setPaySaving(false);
    }
  };

  const handleSaveLeadEstimate = async () => {
    if (!leadEstLead) return;
    setLeadEstSaving(true);
    try {
      const payload = {
        address: leadEstForm.address,
        date: leadEstForm.date,
        time: leadEstForm.time,
        technician: leadEstForm.technician,
      };
      const isLead = leadEstLead.status === 'Lead';
      if (isLead) {
        await crmBackend.createEstimateFromLead(leadEstLead.id, payload);
      } else {
        await crmBackend.createEstimateFromCustomer(leadEstLead.id, payload);
      }
      toast({ title: 'Estimate created' });
      setLeadEstOpen(false);
    } catch (err) {
      toast({ title: 'Failed to create estimate', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setLeadEstSaving(false);
    }
  };

  const openInvoiceDetail = async (inv) => {
    try {
      const result = await crmBackend.getInvoiceDetail(inv.name);
      setSelectedInvoice(result.data || inv);
      setInvoiceDetailOpen(true);
    } catch (err) {
      toast({ title: 'Could not load invoice', description: err.message, variant: 'destructive' });
    }
  };

  useEffect(() => {
    const incoming = location?.state?.addContact;
    if (incoming?.name && incoming?.phone) {
      setActiveTab('contacts');
      setForm((f) => ({
        ...f,
        name: incoming.name || '',
        company: incoming.company === 'Not in CRM' ? '' : (incoming.company || ''),
        phone: incoming.phone || '',
        email: incoming.email || '',
        status: incoming.status || 'Active',
        deal_value: incoming.dealValue ? String(incoming.dealValue) : ''
      }));
      setAddOpen(true);
      // Clear navigation state to prevent re-opening on refresh/back
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);
  
  const { IncomingCallPopup } = use3CXIntegration();

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    navigate('/');
  };

  const handleEmailInvoice = (invoice) => {
    toast({
      title: 'Invoice Emailed',
      description: `Invoice ${invoice.id} has been sent to ${invoice.contactName}`,
    });
  };

  const openAddContact = () => {
    setForm({
      name: '',
      company: '',
      phone: '',
      email: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      status: 'Active',
      deal_value: ''
    });
    setAddrQuery('');
    setAddrSuggestions([]);
    setAddrOpen(false);
    setAddOpen(true);
  };

  const saveContact = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' });
      return;
    }
    try {
      await crmBackend.createLead({
        lead_name: form.name.trim(),
        email_id: form.email?.trim() || undefined,
        mobile_no: form.phone?.trim() || undefined,
        city: form.city?.trim() || form.address_line1?.trim() || undefined,
        state: form.state?.trim() || undefined,
        source: 'Direct',
      });
      toast({ title: 'Contact added' });
      setAddOpen(false);
      await loadDashboard();
    } catch (e) {
      toast({ title: 'Failed to save contact', description: e.message, variant: 'destructive' });
    }
  };

  const autoFillCityZip = async () => {
    if (!API_BASE) {
      toast({ title: 'Missing backend URL', description: 'Set REACT_APP_BACKEND_URL in frontend/.env', variant: 'destructive' });
      return;
    }
    const addr = [
      form.address_line1,
      form.address_line2,
      form.city,
      form.state,
      form.postal_code
    ].filter(Boolean).join(', ');

    if (!form.address_line1.trim()) {
      toast({ title: 'Enter Address line 1 first', variant: 'destructive' });
      return;
    }

    try {
      const { data } = await axios.post(`${API_BASE}/api/address/lookup`, { address: addr });
      setForm((f) => ({
        ...f,
        city: data.city ?? f.city,
        state: data.state ?? f.state,
        postal_code: data.postal_code ?? f.postal_code,
      }));
      if (data.formatted_address) {
        toast({ title: 'Address found', description: data.formatted_address });
      } else {
        toast({ title: 'Auto-filled', description: 'City/State/ZIP updated.' });
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast({ title: 'Address lookup failed', description: msg, variant: 'destructive' });
    }
  };

  useEffect(() => {
    if (!addOpen) return;
    if (!API_BASE) return;

    const q = addrQuery.trim();
    if (q.length < 4) {
      setAddrSuggestions([]);
      setAddrOpen(false);
      return;
    }

    setAddrLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API_BASE}/api/address/suggest`, { query: q });
        setAddrSuggestions(data?.suggestions || []);
        setAddrOpen(true);
      } catch {
        setAddrSuggestions([]);
        setAddrOpen(false);
      } finally {
        setAddrLoading(false);
      }
    }, 350);

    return () => clearTimeout(t);
  }, [addrQuery, API_BASE, addOpen]);

  const selectSuggestion = async (s) => {
    if (!API_BASE) return;
    try {
      const { data } = await axios.get(`${API_BASE}/api/address/details`, { params: { place_id: s.place_id, description: s.description } });
      setForm((f) => ({
        ...f,
        address_line1: data.address_line1 ?? f.address_line1,
        city: data.city ?? f.city,
        state: data.state ?? f.state,
        postal_code: data.postal_code ?? f.postal_code,
      }));
      setAddrQuery(s.description);
      setAddrOpen(false);
      setAddrSuggestions([]);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast({ title: 'Address lookup failed', description: msg, variant: 'destructive' });
    }
  };

  const StatCard = ({ title, value, icon: Icon, trend, detail, color, onClick }) => (
    <Card className={onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''} onClick={onClick}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <h3 className="text-2xl font-bold mt-2">{value}</h3>
            {detail && <p className="text-sm text-gray-500 mt-1">{detail}</p>}
            {trend && (
              <p className="text-sm text-green-600 mt-1 flex items-center">
                <TrendingUp className="w-4 h-4 mr-1" />
                {trend}
              </p>
            )}
          </div>
          <div className={`p-3 rounded-lg ${color}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const getStatusColor = (status) => {
    const colors = {
      'Active': 'bg-green-100 text-green-700',
      'Lead': 'bg-blue-100 text-blue-700',
      'Paid': 'bg-green-100 text-green-700',
      'Pending': 'bg-yellow-100 text-yellow-700',
      'Overdue': 'bg-red-100 text-red-700',
      'Sent': 'bg-blue-100 text-blue-700',
      'Draft': 'bg-gray-100 text-gray-700',
      // Estimate approval-workflow statuses (custom_approval_status)
      'Sent for Approval': 'bg-yellow-100 text-yellow-700',
      'Approved': 'bg-green-100 text-green-700',
      'Declined': 'bg-red-100 text-red-700',
      'Cancelled': 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/40 z-40 md:hidden" 
          onClick={() => setMobileMenuOpen(false)} 
        />
      )}

      {/* Sidebar */}
      <div className={`fixed left-0 top-0 h-full w-64 bg-white border-r border-gray-200 z-50 transition-transform duration-300 transform md:transform-none ${
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
      }`}>
        <div className="p-6">
          <div className="flex items-center space-x-3 mb-8">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                <path d="M8 11h8v2H8z"/>
                <path d="M11 8h2v8h-2z"/>
              </svg>
            </div>
            <span className="text-xl font-bold text-gray-900">AAA Irrigation</span>
          </div>

          <nav className="space-y-1">
            <button
              onClick={() => { setActiveTab('overview'); setMobileMenuOpen(false); window.location.hash = 'overview'; }}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'overview' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <LayoutDashboard className="w-5 h-5" />
              <span className="font-medium">Dashboard</span>
            </button>
            <button
              onClick={() => { setActiveTab('contacts'); setMobileMenuOpen(false); window.location.hash = 'contacts'; }}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'contacts' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Users className="w-5 h-5" />
              <span className="font-medium">Contacts</span>
            </button>
            <button
              onClick={() => { setActiveTab('leads'); setMobileMenuOpen(false); window.location.hash = 'leads'; }}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'leads' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Briefcase className="w-5 h-5" />
              <span className="font-medium">Leads</span>
            </button>
            <button
              onClick={() => { setActiveTab('invoices'); setMobileMenuOpen(false); window.location.hash = 'invoices'; }}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'invoices' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Receipt className="w-5 h-5" />
              <span className="font-medium">Invoices</span>
            </button>
            <button
              onClick={() => { setActiveTab('estimates'); setMobileMenuOpen(false); window.location.hash = 'estimates'; }}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'estimates' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <ClipboardList className="w-5 h-5" />
              <span className="font-medium">Estimates</span>
            </button>
            <button
              onClick={() => { navigate('/calendar'); setMobileMenuOpen(false); }}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors text-gray-700 hover:bg-gray-50"
            >
              <Calendar className="w-5 h-5" />
              <span className="font-medium">Calendar</span>
            </button>
            <button
              onClick={() => { navigate('/map'); setMobileMenuOpen(false); }}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors text-gray-700 hover:bg-gray-50"
            >
              <MapPin className="w-5 h-5" />
              <span className="font-medium">Field Map</span>
            </button>
            <button
              onClick={() => { navigate('/tech/dashboard'); setMobileMenuOpen(false); }}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors text-gray-700 hover:bg-gray-50"
            >
              <Wrench className="w-5 h-5" />
              <span className="font-medium">Tech Portal</span>
            </button>
            <button
              onClick={() => { setActiveTab('analytics'); setMobileMenuOpen(false); window.location.hash = 'analytics'; }}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'analytics' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <BarChart2 className="w-5 h-5" />
              <span className="font-medium">Analytics</span>
            </button>
            <button
              onClick={() => { setActiveTab('bot'); setMobileMenuOpen(false); window.location.hash = 'bot'; }}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'bot' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <img src="/flowbot.png" alt="Bot" className="w-5 h-5 object-contain" />
              <span className="font-medium">Flow Bot AI™</span>
            </button>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="md:ml-64 p-4 md:p-8 min-h-screen">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center">
            <Button 
              variant="outline" 
              size="icon" 
              className="md:hidden mr-3" 
              onClick={() => setMobileMenuOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Dashboard</h1>
              <p className="text-gray-500 mt-1 text-sm md:text-base">Welcome back, {currentUser.name}!</p>
            </div>
          </div>
          <div className="flex items-center space-x-4 w-full md:w-auto justify-between md:justify-end">
            <div className="relative flex-1 md:flex-none">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-full md:w-80"
              />
            </div>
            <Button variant="outline" size="icon" title="Message team" onClick={() => { setChatText(''); setChatOpen(true); }}>
              <MessageCircle className="w-5 h-5" />
            </Button>
            <Button variant="outline" size="icon">
              <Bell className="w-5 h-5" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                  <Settings className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-gray-900">{currentUser.name}</p>
                  <p className="text-xs text-gray-500">{currentUser.role}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Overview Stats */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Total Contacts"
                value={contactsLoading ? '…' : dashboardStats.total_contacts}
                icon={Users}
                trend={dashboardStats.connected ? 'Live from ERPNext' : null}
                color="bg-blue-500"
                onClick={() => setActiveTab('contacts')}
              />
              <StatCard
                title="Active Leads"
                value={contactsLoading ? '…' : dashboardStats.active_leads}
                icon={Briefcase}
                color="bg-green-500"
                onClick={() => setActiveTab('leads')}
              />
              <StatCard
                title="Monthly Revenue"
                value={contactsLoading ? '…' : crmBackend.formatCurrency(dashboardStats.monthly_revenue)}
                icon={DollarSign}
                color="bg-purple-500"
                onClick={() => setActiveTab('analytics')}
              />
              <StatCard
                title="Conversion Rate"
                value={contactsLoading ? '…' : `${dashboardStats.conversion_rate}%`}
                detail={dashboardStats.conversion_leads ? `${dashboardStats.conversion_paid} of ${dashboardStats.conversion_leads} leads became paid jobs` : null}
                icon={TrendingUp}
                color="bg-orange-500"
                onClick={() => setActiveTab('analytics')}
              />
            </div>

            {/* Job figures for the current year: completion, new vs return customers */}
            {dashboardStats.job_metrics && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatCard
                  title={`Completion Rate (${dashboardStats.job_metrics.year})`}
                  value={`${dashboardStats.job_metrics.completion_rate}%`}
                  detail={`${dashboardStats.job_metrics.jobs_completed} completed of ${dashboardStats.job_metrics.jobs_scheduled} scheduled jobs`}
                  icon={CheckCircle}
                  color="bg-emerald-600"
                />
                <StatCard
                  title={`New Customers (${dashboardStats.job_metrics.year})`}
                  value={`${dashboardStats.job_metrics.new_customer_pct}%`}
                  detail={`${dashboardStats.job_metrics.new_customer_jobs} of ${dashboardStats.job_metrics.jobs_scheduled} jobs were a customer's first`}
                  icon={UserPlus}
                  color="bg-sky-600"
                />
                <StatCard
                  title={`Return Customers (${dashboardStats.job_metrics.year})`}
                  value={`${dashboardStats.job_metrics.return_customer_pct}%`}
                  detail={`${dashboardStats.job_metrics.return_customer_jobs} of ${dashboardStats.job_metrics.jobs_scheduled} jobs were repeat customers`}
                  icon={Repeat}
                  color="bg-indigo-600"
                />
              </div>
            )}

            {/* Recent Activity & Top Deals */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Recent Activity</CardTitle>
                  <CardDescription>Latest updates from your team</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {recentActivity.map((item) => (
                      <div key={`${item.type}-${item.id}`} className="flex items-start justify-between pb-3 border-b last:border-0">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{item.title}</p>
                          <p className="text-xs text-gray-500 mt-1">{item.subtitle}</p>
                        </div>
                        {item.time && (
                          <span className="text-xs text-gray-400 whitespace-nowrap ml-2">
                            {new Date(item.time).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    ))}
                    {!contactsLoading && recentActivity.length === 0 && (
                      <p className="text-sm text-gray-500">No recent activity.</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Top Leads</CardTitle>
                  <CardDescription>Highest value opportunities</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {contacts.filter(c => c.status === 'Lead').slice(0, 4).map((lead) => (
                      <div key={lead.id} className="flex items-center justify-between pb-4 border-b last:border-0">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{lead.name}</p>
                          <p className="text-xs text-gray-500 mt-1">{lead.city || lead.state || '—'}</p>
                        </div>
                        <div className="text-right">
                          <Badge className="mt-1" variant="secondary">Lead</Badge>
                        </div>
                      </div>
                    ))}
                    {contacts.filter(c => c.status === 'Lead').length === 0 && (
                      <p className="text-sm text-gray-500">No leads yet.</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Contacts</h2>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={openAddContact}>
                <Plus className="w-4 h-4 mr-2" />
                Add Contact
              </Button>
            </div>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by name, last name, or phone…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Card>
              <CardContent className="p-6">
                <div className="space-y-4">
                  {loadingAllCustomers && (
                    <div className="text-sm text-gray-500">Loading all contacts…</div>
                  )}
                  {contactsLoading && (
                    <div className="text-sm text-gray-500">Loading contacts…</div>
                  )}
                  {!contactsLoading && filteredContacts.length === 0 && (
                    <div className="text-sm text-gray-500">
                      {searchQuery ? `No contacts match "${searchQuery}".` : 'No contacts yet. Click "Add Contact".'}
                    </div>
                  )}
                  {!contactsLoading && filteredContacts.map((contact) => (
                    <div key={contact.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/contacts/${encodeURIComponent(contact.id)}`, { state: { contact } })}
                    >
                      <div className="flex items-center space-x-4">
                        <Avatar className="h-12 w-12">
                          <AvatarFallback className="bg-blue-500 text-white text-lg">
                            {(contact.name || 'C').substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-semibold text-gray-900">{contact.name}</p>
                          <p className="text-sm text-gray-500">{contact.company || '—'}</p>
                          <div className="flex items-center space-x-4 mt-1">
                            {contact.email && (
                              <span className="text-xs text-gray-500 flex items-center">
                              <Mail className="w-3 h-3 mr-1" />
                              {contact.email}
                              </span>
                            )}
                            {contact.phone && (
                              <a href={`tel:${contact.phone}`} onClick={(e) => { e.stopPropagation(); }}
                                className="text-xs text-green-700 flex items-center hover:text-green-900">
                                <Phone className="w-3 h-3 mr-1" />
                                {contact.phone}
                              </a>
                            )}
                          </div>
                          {(contact.address_line1 || contact.city || contact.state || contact.postal_code) && (
                            <div className="text-xs text-gray-400 mt-1">
                              {[contact.address_line1, contact.address_line2].filter(Boolean).join(' ')}
                              {contact.city || contact.state || contact.postal_code ? (
                                <div>
                                  {[contact.city, contact.state, contact.postal_code].filter(Boolean).join(', ')}
                                </div>
                              ) : null}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <Badge className={getStatusColor(contact.status || 'Active')}>{contact.status || 'Active'}</Badge>
                          {typeof contact.deal_value === 'number' && (
                            <p className="text-sm font-medium text-gray-900 mt-2">
                              ${contact.deal_value.toLocaleString()}
                            </p>
                          )}
                        </div>
                        <Button variant="ghost" size="icon" onClick={(e) => e.stopPropagation()}>
                          <MoreVertical className="w-5 h-5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add contact</DialogTitle>
            </DialogHeader>

            <div className="space-y-3">
              <div>
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <Label>Company</Label>
                <Input value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label>Address line 1</Label>
                <div className="relative">
                  <Input
                    value={form.address_line1}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm((f) => ({ ...f, address_line1: v }));
                      setAddrQuery(v);
                      if (v.trim().length >= 4) setAddrOpen(true);
                    }}
                    onFocus={() => {
                      if (addrSuggestions.length) setAddrOpen(true);
                    }}
                    onBlur={() => {
                      // small delay so clicks on suggestions register
                      setTimeout(() => setAddrOpen(false), 150);
                    }}
                    placeholder="Start typing an address…"
                  />

                  {(addrLoading || (addrOpen && addrSuggestions.length > 0)) && (
                    <div className="absolute z-50 left-0 right-0 mt-1 bg-white border rounded-md shadow-lg overflow-hidden">
                      {addrLoading && (
                        <div className="px-3 py-2 text-xs text-gray-500">Searching…</div>
                      )}
                      {!addrLoading && addrSuggestions.map((s) => (
                        <button
                          key={s.place_id}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectSuggestion(s)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                        >
                          {s.description}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <Label>Address line 2</Label>
                <Input value={form.address_line2} onChange={(e) => setForm((f) => ({ ...f, address_line2: e.target.value }))} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>City</Label>
                  <Input value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} />
                </div>
                <div>
                  <Label>State</Label>
                  <Input value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))} />
                </div>
                <div>
                  <Label>ZIP</Label>
                  <Input value={form.postal_code} onChange={(e) => setForm((f) => ({ ...f, postal_code: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Status</Label>
                  <Input value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} />
                </div>
                <div>
                  <Label>Deal value</Label>
                  <Input
                    inputMode="decimal"
                    placeholder="e.g. 2500"
                    value={form.deal_value}
                    onChange={(e) => setForm((f) => ({ ...f, deal_value: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button onClick={saveContact} className="bg-blue-600 hover:bg-blue-700">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Leads Tab */}
        {activeTab === 'leads' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Leads</h2>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Lead
              </Button>
            </div>
            <Card>
              <CardContent className="p-6">
                <div className="space-y-4">
                  {contacts.filter(c => c.status === 'Lead').map((lead) => (
                    <div key={lead.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => {
                        setLeadEstLead(lead);
                        setLeadEstForm({ address: [lead.address_line1, lead.city, lead.state].filter(Boolean).join(', '), date: '', time: '8AM-11AM', technician: '' });
                        setLeadEstOpen(true);
                      }}
                    >
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{lead.name}</p>
                        <p className="text-sm text-gray-500 mt-1">
                          {lead.email || '—'} •{' '}
                          {lead.phone
                            ? <a href={`tel:${lead.phone}`} onClick={(e) => e.stopPropagation()} className="text-green-700 hover:text-green-900">{lead.phone}</a>
                            : '—'}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">{[lead.city, lead.state].filter(Boolean).join(', ') || '—'}</p>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Badge className="bg-blue-100 text-blue-700">Lead</Badge>
                        <Button variant="outline" size="sm" className="text-green-700 border-green-300 hover:bg-green-50"
                          onClick={(e) => { e.stopPropagation(); setLeadEstLead(lead); setLeadEstForm({ address: [lead.address_line1, lead.city, lead.state].filter(Boolean).join(', '), date: '', time: '8AM-11AM', technician: '' }); setLeadEstOpen(true); }}>
                          <Calendar className="w-4 h-4 mr-1" /> Schedule
                        </Button>
                      </div>
                    </div>
                  ))}
                  {contacts.filter(c => c.status === 'Lead').length === 0 && (
                    <p className="text-sm text-gray-500">No leads yet.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Invoices</h2>
              <Button variant="outline" size="sm" onClick={loadInvoices}>Refresh</Button>
            </div>
            <Card>
              <CardContent className="p-6">
                <div className="space-y-3">
                  {invoicesLoading && <p className="text-sm text-gray-500">Loading invoices…</p>}
                  {!invoicesLoading && invoices.length === 0 && (
                    <p className="text-sm text-gray-500">No invoices found.</p>
                  )}
                  {!invoicesLoading && invoices.map((inv) => (
                    <div key={inv.name} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer" onClick={() => openInvoiceDetail(inv)}>
                      <div>
                        <p className="font-semibold text-gray-900">{inv.name}</p>
                        <p className="text-sm text-gray-500">{inv.customer_name || inv.customer}</p>
                        <p className="text-xs text-gray-400 mt-1">Due: {inv.due_date || inv.posting_date || '—'}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">{crmBackend.formatCurrency(inv.grand_total)}</p>
                        {inv.outstanding_amount > 0 && (
                          <p className="text-xs text-red-500">Outstanding: {crmBackend.formatCurrency(inv.outstanding_amount)}</p>
                        )}
                        <Badge className={`mt-1 ${getStatusColor(inv.status)}`}>{inv.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Estimates Tab */}
        {activeTab === 'estimates' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Estimates</h2>
              <Button variant="outline" size="sm" onClick={loadEstimates}>Refresh</Button>
            </div>
            <Card>
              <CardContent className="p-6">
                <div className="space-y-3">
                  {estimatesLoading && <p className="text-sm text-gray-500">Loading estimates…</p>}
                  {!estimatesLoading && estimates.length === 0 && (
                    <p className="text-sm text-gray-500">No estimates found.</p>
                  )}
                  {!estimatesLoading && estimates.map((est) => (
                    <div key={est.name} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer" onClick={() => openEstimateDetail(est)}>
                      <div>
                        <p className="font-semibold text-gray-900">{est.name}</p>
                        <p className="text-sm text-gray-500">{est.party_name || est.customer_name}</p>
                        {est.custom_service_address && (
                          <p className="text-xs text-gray-400 mt-1">{est.custom_service_address.split('\n')[0]}</p>
                        )}
                        {est.custom_scheduled_date && (
                          <p className="text-xs text-gray-400">Scheduled: {est.custom_scheduled_date}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">{crmBackend.formatCurrency(est.grand_total)}</p>
                        <Badge className={`mt-1 ${est.custom_pipeline_status
                          ? crmBackend.pipelineStatusColor(est.custom_pipeline_status)
                          : getStatusColor(est.custom_approval_status || est.status)}`}>
                          {est.custom_pipeline_status || est.custom_approval_status || est.status || 'Draft'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Analytics</h2>
            {analyticsLoading && <p className="text-gray-500">Loading…</p>}
            {analytics && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-gray-500">Open Jobs</p>
                    <p className="text-3xl font-bold text-orange-600">{analytics.open_jobs ?? 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-gray-500">Completed Jobs</p>
                    <p className="text-3xl font-bold text-green-600">{analytics.completed_jobs ?? 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-gray-500">Total Customers</p>
                    <p className="text-3xl font-bold text-blue-600">{analytics.total_customers ?? 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-gray-500">Open Leads</p>
                    <p className="text-3xl font-bold text-gray-700">{analytics.total_leads ?? 0}</p>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* Bot Tab */}
        {activeTab === 'bot' && (
          <div className="flex flex-col" style={{ height: 'calc(100vh - 64px)' }}>
            <iframe
              ref={botIframeRef}
              onLoad={handleBotIframeLoad}
              src="https://bot.aaairrigationservice.com"
              title="Flow Bot AI™"
              className="flex-1 w-full border-0"
              style={{ minHeight: '600px' }}
              allow="microphone; camera"
            />
          </div>
        )}
      </div>

      {/* Estimate Detail / Edit Dialog — extracted to components/EstimateDetailDialog.jsx (Phase 2B) */}
      <EstimateDetailDialog
        open={estimateDetailOpen}
        onOpenChange={setEstimateDetailOpen}
        estimate={selectedEstimate}
        editForm={estimateEditForm}
        onEditFormChange={setEstimateEditForm}
        sending={sendingEstimate}
        onSend={handleSendEstimate}
        completing={completingJob}
        onCompleteJob={handleCompleteEstimateJob}
        saving={estimateSaving}
        onSave={saveEstimate}
        getStatusColor={getStatusColor}
        onItemsChanged={refreshSelectedEstimate}
      />

      {/* Invoice Detail Dialog — extracted to components/InvoiceDetailDialog.jsx (Phase 2B) */}
      <InvoiceDetailDialog
        open={invoiceDetailOpen}
        onOpenChange={setInvoiceDetailOpen}
        invoice={selectedInvoice}
        sending={sendingInvoice}
        onSend={handleSendInvoice}
        onRecordPayment={handleOpenPayment}
        onProcessPayment={(inv) => setChargeInvoice(inv)}
        getStatusColor={getStatusColor}
      />

      {/* Lead → Estimate Dialog */}
      <Dialog open={leadEstOpen} onOpenChange={setLeadEstOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Create Estimate — {leadEstLead?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Service Address</Label>
              <Input
                value={leadEstForm.address}
                placeholder="123 Main St, City, TX"
                onChange={(e) => setLeadEstForm((f) => ({ ...f, address: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={leadEstForm.date}
                  onChange={(e) => setLeadEstForm((f) => ({ ...f, date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Time Window</Label>
                <div className="grid grid-cols-2 gap-1 mt-1">
                  {crmBackend.TIME_WINDOWS.map(w => (
                    <button key={w} type="button"
                      onClick={() => setLeadEstForm(f => ({ ...f, time: w }))}
                      className={`h-8 rounded text-xs font-medium border transition-colors ${
                        leadEstForm.time === w
                          ? 'bg-green-600 text-white border-green-600'
                          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                      }`}>
                      {w}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <TechnicianMultiSelect
                value={leadEstForm.technician}
                onChange={(val) => setLeadEstForm((f) => ({ ...f, technician: val }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLeadEstOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveLeadEstimate} disabled={leadEstSaving} className="bg-green-600 hover:bg-green-700">
              {leadEstSaving ? 'Creating…' : 'Create Estimate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          {payInvoice && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">{payInvoice.name} — {payInvoice.customer_name || payInvoice.customer}</p>
              <div>
                <Label>Amount ($)</Label>
                <Input
                  type="number" min="0" step="0.01"
                  value={payForm.amount}
                  onChange={(e) => setPayForm((f) => ({ ...f, amount: e.target.value }))}
                />
              </div>
              <div>
                <Label>Payment Method</Label>
                <select
                  className="w-full mt-1 border rounded-md px-3 py-2 text-sm"
                  value={payForm.mode}
                  onChange={(e) => setPayForm((f) => ({ ...f, mode: e.target.value }))}
                >
                  <option>Cash</option>
                  <option>Check</option>
                  <option>Credit Card</option>
                  <option>Zelle</option>
                </select>
              </div>
              <div>
                <Label>Reference / Check No.</Label>
                <Input
                  placeholder={
                    payForm.mode === 'Check' ? 'Check number'
                      : payForm.mode === 'Credit Card' ? 'Auth code or last 4 digits'
                      : payForm.mode === 'Zelle' ? 'Confirmation number'
                      : 'Optional'
                  }
                  value={payForm.reference_no}
                  onChange={(e) => setPayForm((f) => ({ ...f, reference_no: e.target.value }))}
                />
                {/* This dialog RECORDS a payment already taken elsewhere (terminal,
                    phone, cash) — it does not charge a card. To actually charge one,
                    use "Process Payment" on the invoice. */}
                {payForm.mode === 'Credit Card' && (
                  <p className="mt-1 text-xs text-gray-500">
                    Records a card payment already taken on a terminal. To actually charge
                    a card now, close this and use <strong>Process Payment</strong> instead.
                  </p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
            <Button onClick={handleSavePayment} disabled={paySaving} className="bg-green-600 hover:bg-green-700">
              {paySaving ? 'Saving…' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Process Payment — live Authorize.net card charge (Accept.js tokenized) */}
      {chargeInvoice && (
        <ProcessPaymentDialog
          invoice={chargeInvoice}
          surface="Admin App"
          onClose={() => setChargeInvoice(null)}
          onSuccess={async () => { setInvoices([]); await loadInvoices(); }}
        />
      )}

      {/* Admin → Team chat dialog */}
      <Dialog open={chatOpen} onOpenChange={setChatOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Message Team</DialogTitle>
          </DialogHeader>
          <textarea
            className="w-full border rounded-md p-2 text-sm resize-none"
            rows={4}
            placeholder="Broadcast to field team via Synology Chat…"
            value={chatText}
            onChange={e => setChatText(e.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setChatOpen(false)}>Cancel</Button>
            <Button
              disabled={chatSending || !chatText.trim()}
              className="bg-green-600 hover:bg-green-700"
              onClick={async () => {
                setChatSending(true);
                try {
                  await crmBackend.sendChatMessage(chatText.trim(), currentUser.name, '');
                  toast({ title: 'Message sent' });
                  setChatOpen(false);
                } catch (err) {
                  toast({ title: 'Send failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
                } finally {
                  setChatSending(false);
                }
              }}
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              {chatSending ? 'Sending…' : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Dashboard;